import { useEffect, useRef, useCallback, useState } from "react";
import { WS_BASE, api } from "@/lib/api";

// Single client websocket to the Cadence relay. Mirrors Vercel AI's streaming
// message pipeline: one socket, handlers fan out task.event deltas to the UI.
export function useCadenceSocket(onMessage) {
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  useEffect(() => {
    // Gate on being logged in, but the JWT itself never goes on the wire — it
    // only authenticates the ws-ticket fetch below (via the api interceptor).
    if (!localStorage.getItem("cadence_token")) return;
    let alive = true;
    let pingTimer = null;

    async function connect() {
      if (!alive) return;
      // Fetch a short-lived, single-use WS ticket before EVERY connect attempt
      // (audit F5): the long-lived JWT must never appear in the WS URL. On a
      // failed fetch (offline / 401), retry on the same backoff the socket uses
      // instead of crashing or falling back to the token.
      let ticket;
      try {
        const { data } = await api.post("/auth/ws-ticket");
        ticket = data && data.ticket;
      } catch (e) {
        if (alive) setTimeout(connect, 2000);
        return;
      }
      if (!alive || !ticket) return;
      const ws = new WebSocket(`${WS_BASE}/api/ws/client?ticket=${ticket}`);
      wsRef.current = ws;
      ws.onopen = () => {
        if (!alive) return;
        setConnected(true);
        pingTimer = setInterval(() => {
          try { ws.send(JSON.stringify({ type: "ping" })); } catch (e) {}
        }, 25000);
      };
      ws.onmessage = (ev) => {
        try { handlerRef.current && handlerRef.current(JSON.parse(ev.data)); } catch (e) {}
      };
      ws.onclose = () => {
        setConnected(false);
        if (pingTimer) clearInterval(pingTimer);
        if (alive) setTimeout(connect, 2000);
      };
      ws.onerror = () => { try { ws.close(); } catch (e) {} };
    }
    connect();
    return () => {
      alive = false;
      if (pingTimer) clearInterval(pingTimer);
      try { wsRef.current && wsRef.current.close(); } catch (e) {}
    };
  }, []);

  const send = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
      return true;
    }
    return false;
  }, []);

  return { send, connected };
}
